import { apiClient } from './apiClient';

export type PricingFeatureKind = 'BooleanFlag' | 'Counter' | 'CounterProgressive';
export type PricingBillingType = 'Monthly' | 'Yearly' | 'OneTime' | 'Usage';
export type PricingQuoteStatus = 'Draft' | 'Sent' | 'Accepted' | 'Rejected' | 'Archived';

export type PricingDiscountTier = {
  minQuantity: number;
  maxQuantity?: number | null;
  multiplier: number;
  sortOrder: number;
};

export type PricingSettings = {
  id: string;
  defaultMarginPercent: number;
  defaultTaxRatePercent: number;
  currency: string;
  updatedAtUtc: string;
};

export type PricingSettingsUpsert = {
  defaultMarginPercent: number;
  defaultTaxRatePercent: number;
  currency?: string;
};

export type PricingFeature = {
  id: string;
  salesProductId?: string | null;
  code: string;
  name: string;
  description: string;
  featureKind: PricingFeatureKind | string;
  billingType: PricingBillingType | string;
  costSetup: number;
  costUnit: number;
  includedQuantity: number;
  hasVolumeDiscount: boolean;
  notes: string;
  isActive: boolean;
  sortOrder: number;
  discountTiers: PricingDiscountTier[];
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type PricingFeatureUpsert = {
  salesProductId?: string | null;
  name: string;
  code: string;
  description: string;
  featureKind: PricingFeatureKind | string;
  billingType: PricingBillingType | string;
  costSetup: number;
  costUnit: number;
  includedQuantity: number;
  hasVolumeDiscount: boolean;
  notes?: string;
  isActive: boolean;
  sortOrder: number;
  discountTiers: PricingDiscountTier[];
};

export type PricingFeatureListResult = {
  items: PricingFeature[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type PricingSimulateLine = {
  pricingFeatureId: string;
  enabled: boolean;
  quantity: number;
};

export type PricingSimulateProfessionalLine = {
  name: string;
  quantity: number;
  unitCost: number;
  isRecurring: boolean;
};

export type PricingSimulateRequest = {
  salesProductId?: string | null;
  secondarySalesProductId?: string | null;
  contractMonths: number;
  marginPercent?: number | null;
  taxRatePercent?: number | null;
  desiredProfitAmount?: number | null;
  lines: PricingSimulateLine[];
  professionalLines?: PricingSimulateProfessionalLine[];
};

export type PricingSimulationLine = {
  pricingFeatureId?: string | null;
  lineKind: string;
  name: string;
  isEnabled: boolean;
  quantity: number;
  costSetup: number;
  costMrr: number;
  sellSetup: number;
  sellMrr: number;
  sortOrder: number;
};

export type PricingSimulationTotals = {
  costSetupTotal: number;
  costMrrTotal: number;
  sellSetupTotal: number;
  sellMrrTotal: number;
  taxAmountSetup: number;
  taxAmountMrr: number;
  profitAmountSetup: number;
  profitAmountMrr: number;
  annualTotal: number;
  contractTotal: number;
};

export type PricingSimulationResult = {
  salesProductId?: string | null;
  secondarySalesProductId?: string | null;
  contractMonths: number;
  marginPercent: number;
  taxRatePercent: number;
  desiredProfitAmount?: number | null;
  currency: string;
  lines: PricingSimulationLine[];
  totals: PricingSimulationTotals;
};

export type PricingQuoteCreate = PricingSimulateRequest & {
  title: string;
  companyId?: string | null;
  prospectId?: string | null;
  salesOpportunityId?: string | null;
  marketSegmentId?: string | null;
  notes?: string;
};

export type PricingQuote = {
  id: string;
  quoteNumber: string;
  title: string;
  status: PricingQuoteStatus | string;
  salesProductId?: string | null;
  secondarySalesProductId?: string | null;
  companyId?: string | null;
  prospectId?: string | null;
  salesOpportunityId?: string | null;
  marketSegmentId?: string | null;
  contractMonths: number;
  marginPercent: number;
  taxRatePercent: number;
  desiredProfitAmount?: number | null;
  costSetupTotal: number;
  costMrrTotal: number;
  sellSetupTotal: number;
  sellMrrTotal: number;
  taxAmountSetup: number;
  taxAmountMrr: number;
  profitAmountSetup: number;
  profitAmountMrr: number;
  annualTotal: number;
  notes: string;
  lines: PricingSimulationLine[];
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type PricingQuoteListResult = {
  items: PricingQuote[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export async function getPricingSettings(): Promise<PricingSettings> {
  const { data } = await apiClient.get<PricingSettings>('/v1/sales/pricing/settings');
  return data;
}

export async function updatePricingSettings(payload: PricingSettingsUpsert): Promise<PricingSettings> {
  const { data } = await apiClient.put<PricingSettings>('/v1/sales/pricing/settings', payload);
  return data;
}

export async function listPricingFeatures(params: {
  salesProductId?: string;
  activeOnly?: boolean;
  page?: number;
  pageSize?: number;
} = {}): Promise<PricingFeatureListResult> {
  const { data } = await apiClient.get<PricingFeatureListResult>('/v1/sales/pricing/features', { params });
  return data;
}

export async function createPricingFeature(payload: PricingFeatureUpsert): Promise<PricingFeature> {
  const { data } = await apiClient.post<PricingFeature>('/v1/sales/pricing/features', payload);
  return data;
}

export async function updatePricingFeature(
  id: string,
  payload: PricingFeatureUpsert,
): Promise<PricingFeature> {
  const { data } = await apiClient.put<PricingFeature>(`/v1/sales/pricing/features/${id}`, payload);
  return data;
}

export async function deletePricingFeature(id: string): Promise<void> {
  await apiClient.delete(`/v1/sales/pricing/features/${id}`);
}

export async function simulatePricing(payload: PricingSimulateRequest): Promise<PricingSimulationResult> {
  const { data } = await apiClient.post<PricingSimulationResult>('/v1/sales/pricing/simulate', payload);
  return data;
}

export async function listPricingQuotes(params: {
  salesOpportunityId?: string;
  companyId?: string;
  prospectId?: string;
  page?: number;
  pageSize?: number;
} = {}): Promise<PricingQuoteListResult> {
  const { data } = await apiClient.get<PricingQuoteListResult>('/v1/sales/pricing/quotes', { params });
  return data;
}

export async function getPricingQuote(id: string): Promise<PricingQuote> {
  const { data } = await apiClient.get<PricingQuote>(`/v1/sales/pricing/quotes/${id}`);
  return data;
}

export async function createPricingQuote(payload: PricingQuoteCreate): Promise<PricingQuote> {
  const { data } = await apiClient.post<PricingQuote>('/v1/sales/pricing/quotes', payload);
  return data;
}

export async function updatePricingQuoteStatus(
  id: string,
  status: PricingQuoteStatus | string,
): Promise<PricingQuote> {
  const { data } = await apiClient.put<PricingQuote>(`/v1/sales/pricing/quotes/${id}/status`, { status });
  return data;
}
